import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { TripRole, UserData } from '../firebase/firebase.types';
import { updateRolesInTrip } from '../firebase/trips.utils';
import { getUserDocumentsWithEmails, getUserDocumentsWithIds } from '../firebase/users.utils';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { updateTripRoles, updateTripUserEmails } from '../redux/trip/trip.slice';
import { selectCurrentUser } from '../redux/user/user.selectors';
import { UserAndRole } from '../types/TripShare';
import useToast from './useToast';

/** Shapes a `users/{uid}` document for the share UI; a missing email shows as empty. */
const toUserAndRole = (userId: string, data: UserData, role: TripRole): UserAndRole => ({
  userId,
  role,
  email: data.email ?? '',
  displayName: data.displayName ?? '',
  status: data.status === 'deactivated' ? 'deactivated' : 'active',
});

export const useUserRoles = (tripId: string, tripRoles: Record<string, TripRole>) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  // Only members may see member details (e.g. not viewers of a public trip), so don't ask.
  const isMember = Boolean(currentUser && currentUser.uid in tripRoles);
  const [userRoles, setUserRoles] = useState<UserAndRole[]>([]);
  const [originalUserRoles, setOriginalUserRoles] = useState<UserAndRole[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();
  // Saving diffs against the loaded member list, so never save without one.
  const [hasLoadedMembers, setHasLoadedMembers] = useState(false);

  const hasPendingChanges = !isEqual(userRoles, originalUserRoles);

  useEffect(() => {
    let isSubscribed = true;

    const fetchUserRoles = async () => {
      let usersDocs: Awaited<ReturnType<typeof getUserDocumentsWithIds>>;
      try {
        usersDocs = await getUserDocumentsWithIds(Object.keys(tripRoles));
      } catch {
        // A transient failure shouldn't look like an empty trip. Keep state; saving stays blocked.
        return;
      }
      const roles: UserAndRole[] = [];
      usersDocs.forEach((userDoc) => {
        const userAndRole = toUserAndRole(
          userDoc.id,
          userDoc.data() as UserData,
          tripRoles[userDoc.id]
        );
        if (userAndRole.role === TripRole.OWNER) {
          roles.unshift(userAndRole);
        } else {
          roles.push(userAndRole);
        }
      });

      if (isSubscribed) {
        setUserRoles(roles);
        // A member without a stored email yet still gets an avatar, from their name.
        dispatch(updateTripUserEmails(roles.map((user) => user.email || user.displayName)));
        setOriginalUserRoles(roles);
        setHasLoadedMembers(true);
      }
    };

    if (tripRoles && isMember) {
      fetchUserRoles();
    }

    return () => {
      isSubscribed = false;
    };
  }, [dispatch, isMember, tripRoles]);

  const saveChanges = async () => {
    if (!hasLoadedMembers) {
      setIsSaving(false);
      toastError("Couldn't load this trip's members. Refresh the page and try again.");
      return false;
    }
    const rolesToUpdate = userRoles
      .filter(
        (userRole) =>
          !(userRole.userId in tripRoles) || userRole.role !== tripRoles[userRole.userId]
      )
      .map(({ userId, role }) => ({ userId, role }));

    const usersToDelete = Object.keys(tripRoles).filter(
      (userId) => !userRoles.find((userRole) => userRole.userId === userId)
    );

    try {
      await updateRolesInTrip(tripId, rolesToUpdate, usersToDelete);
      dispatch(updateTripRoles({ tripId, rolesToUpdate, usersToDelete }));
      toastSuccess('Your trip share settings were successfully updated.');
      return true;
    } catch (_error) {
      toastError('There was an error saving your share settings. Please try again later.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    userRoles,
    setUserRoles,
    originalUserRoles,
    hasPendingChanges,
    isSaving,
    setIsSaving,
    saveChanges,
  };
};

export const useEmailValidation = (
  userRoles: UserAndRole[],
  setUserRoles: React.Dispatch<React.SetStateAction<UserAndRole[]>>
) => {
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const handleAddNewUsers = useCallback(
    async (email: string) => {
      try {
        const userDocs = await getUserDocumentsWithEmails([email.trim().toLowerCase()]);
        // Unverified sign-ups can't be invited: anyone can register with someone else's email.
        // Legacy docs without the field predate verification and stay invitable.
        const invitableDocs = userDocs.docs.filter(
          (userDoc) =>
            (userDoc.data() as UserData & { emailVerified?: boolean }).emailVerified !== false &&
            userDoc.data().status !== 'deactivated'
        );

        if (invitableDocs.length === 0) {
          setEmailError('No account with this email address.');
          return;
        }
        const [invitee] = invitableDocs;
        if (userRoles.some((userRole) => userRole.userId === invitee.id)) {
          setEmailError(`${email} already has access to this trip`);
          return;
        }
        setUserRoles((prevRoles: UserAndRole[]) => [
          ...prevRoles,
          toUserAndRole(invitee.id, invitee.data() as UserData, TripRole.EDITOR),
        ]);
        setEmailInput('');
      } catch {
        setEmailError('Could not look up that email. Try again.');
      } finally {
        setIsCheckingEmail(false);
      }
    },
    [userRoles, setUserRoles]
  );

  useEffect(() => {
    if (isCheckingEmail) {
      handleAddNewUsers(emailInput);
    }
  }, [emailInput, isCheckingEmail, handleAddNewUsers]);

  return {
    emailInput,
    setEmailInput,
    emailError,
    setEmailError,
    isCheckingEmail,
    setIsCheckingEmail,
  };
};
